/**
 * BentoBox — flexible grid layout for bento-box-style cell configurations.
 *
 * Built on the Grid composite. Renders a configurable grid; children are
 * BentoBox.Cell components that can span 1–3 columns and 1–2 rows. All styling
 * uses EDU Design System tokens. Responsive: fewer columns on smaller viewports.
 */

import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import { Grid, type GridProps } from '../Grid';
import {
  bentoCellVariants,
  type BentoCellVariantProps,
} from './bentobox.variants';

export interface BentoBoxProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of grid columns at full width (1–3). Layout collapses on smaller viewports. */
  columns?: 1 | 2 | 3;
  /** Gap between cells (same as Grid gap). Default: sm. */
  gap?: GridProps['gap'];
  /** Child cells; use BentoBox.Cell. */
  children: React.ReactNode;
}

export interface BentoBoxCellProps
  extends React.HTMLAttributes<HTMLDivElement>,
    BentoCellVariantProps {
  /** Number of columns to span (1–3). Default: 1. */
  colSpan?: 1 | 2 | 3;
  /** Number of rows to span (1–2). Default: 1. */
  rowSpan?: 1 | 2;
  /** Cell background: dark (neutral-900), primary, or secondary. Default: dark. */
  variant?: 'dark' | 'primary' | 'secondary';
  /** Cell content (e.g. value label + content label). */
  children: React.ReactNode;
}

const Cell = React.forwardRef<HTMLDivElement, BentoBoxCellProps>(
  (
    {
      className,
      colSpan = 1,
      rowSpan = 1,
      variant = 'dark',
      children,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      role='group'
      className={cn(
        bentoCellVariants({ variant, colSpan, rowSpan }),
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);
Cell.displayName = 'BentoBox.Cell';

/**
 * BentoBoxComponent — compound component type (root + Cell).
 * Matches the pattern used by Footer and other composites.
 */
interface BentoBoxComponent
  extends React.ForwardRefExoticComponent<
    BentoBoxProps & React.RefAttributes<HTMLDivElement>
  > {
  Cell: typeof Cell;
}

export const BentoBox: BentoBoxComponent = React.forwardRef<
  HTMLDivElement,
  BentoBoxProps
>(({ className, columns = 3, gap = 'sm', children, ...props }, ref) => (
  <Grid
    ref={ref}
    className={cn('w-full', className)}
    columns={1}
    columns-md={columns === 3 ? 2 : undefined}
    columns-lg={columns >= 2 ? columns : undefined}
    gap={gap}
    {...props}
  >
    {children}
  </Grid>
)) as BentoBoxComponent;
BentoBox.displayName = 'BentoBox';

// Attach sub-components to BentoBox for compound component pattern
BentoBox.Cell = Cell;
